import { useEffect, useState } from "react"

import { adminApiService } from "../services/admin-api-service"
import type { AuthOrganization, AuthUser } from "../types"

/**
 * Hook para obter usuário com informações da organização
 */
export function useUserWithOrganization(userId: string) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [organization, setOrganization] = useState<AuthOrganization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchUserAndOrganization = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Buscar dados do usuário
        const userResponse = await adminApiService.getResource<AuthUser>("users", userId)
        const userData = userResponse.data
        setUser(userData)

        // Buscar dados da organização se existir organization_id
        if (userData.organization_id) {
          try {
            const orgResponse = await adminApiService.getResource<AuthOrganization>(
              "organizations",
              userData.organization_id
            )
            setOrganization(orgResponse.data)
          } catch (orgError) {
            console.warn("Failed to fetch organization:", orgError)
            // Não falha se não conseguir buscar a organização
            setOrganization(null)
          }
        } else {
          setOrganization(null)
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to fetch user data")
        setError(error)
        setUser(null)
        setOrganization(null)
      } finally {
        setLoading(false)
      }
    }

    fetchUserAndOrganization()
  }, [userId])

  const refetch = () => {
    if (userId) {
      // Re-trigger the effect
      setLoading(true)
    }
  }

  return {
    user,
    organization,
    loading,
    error,
    refetch,
  }
}
